/**
 * Request/Response Interceptors for Unified Services
 * Handles authentication, logging, error transformation, and more
 */

import { nanoid } from "nanoid";

import { createLogger } from "@/utils/logger";

import {
  AuthenticationError,
  ErrorHandler,
  UnifiedServiceError,
} from "./errors";
import {
  RequestInterceptor,
  RequestOptions,
  ResponseInterceptor,
  ServiceError,
  ServiceErrorCode,
  ServiceResponse,
} from "./types";

const logger = createLogger("service-interceptors");

/**
 * Authentication interceptor for adding auth headers
 */
export class AuthInterceptor implements RequestInterceptor {
  name = "auth";
  priority = 100;

  constructor(private getAuthToken: () => string | null = () => null) {}

  async onRequest(
    config: RequestOptions & { url: string },
  ): Promise<RequestOptions & { url: string }> {
    const token = this.getAuthToken();

    if (token) {
      config.headers = {
        ...config.headers,
        Authorization: `Bearer ${token}`,
      };
    }

    return config;
  }

  async onError(error: ServiceError): Promise<ServiceError> {
    const normalized = ErrorHandler.normalize(error);
    // Handle token refresh on 401 errors
    if (normalized.status === 401) {
      return new AuthenticationError(
        "Authentication token expired or invalid",
        {
          service: normalized.service,
          context: normalized.context,
          details: normalized.details,
        },
      );
    }
    return normalized;
  }
}

/**
 * Logging interceptor for request/response tracking
 */
export class LoggingInterceptor
  implements RequestInterceptor, ResponseInterceptor
{
  name = "logging";
  priority = 50;

  private generateRequestId(): string {
    return nanoid(10);
  }

  async onRequest(
    config: RequestOptions & { url: string },
  ): Promise<RequestOptions & { url: string }> {
    const requestId = this.generateRequestId();

    // Add request ID to headers for tracking
    config.headers = {
      ...config.headers,
      "X-Request-ID": requestId,
    };

    // logger.debug("HTTP Request", {
    //   requestId,
    //   method: "GET", // Default, will be overridden by actual method
    //   url: config.url,
    //   headers: this.sanitizeHeaders(config.headers || {}),
    //   timestamp: new Date().toISOString(),
    // });

    return config;
  }

  async onResponse<T>(
    response: ServiceResponse<T>,
  ): Promise<ServiceResponse<T>> {
    const requestId = response.meta?.timing?.requestId || "unknown";

    // logger.debug("HTTP Response", {
    //   requestId,
    //   status: response.status,
    //   statusText: response.statusText,
    //   duration: response.meta?.timing?.duration,
    //   timestamp: new Date().toISOString(),
    // });

    return response;
  }

  async onError(error: ServiceError): Promise<ServiceError> {
    const normalized = ErrorHandler.normalize(error);
    logger.error("HTTP Error", {
      requestId: normalized.requestId,
      service: normalized.service,
      code: normalized.code,
      status: normalized.status,
      message: normalized.message,
      context: normalized.context,
      timestamp: normalized.timestamp,
    });

    return normalized;
  }

  private sanitizeHeaders(
    headers: Record<string, string>,
  ): Record<string, string> {
    const sanitized = { ...headers };
    const sensitiveHeaders = ["authorization", "cookie", "x-api-key"];

    sensitiveHeaders.forEach((header) => {
      if (sanitized[header] || sanitized[header.toLowerCase()]) {
        sanitized[header] = "[REDACTED]";
      }
    });

    return sanitized;
  }
}

/**
 * Timing interceptor for performance metrics
 */
export class TimingInterceptor
  implements RequestInterceptor, ResponseInterceptor
{
  name = "timing";
  priority = 10;

  private requestTimings = new Map<string, number>();

  async onRequest(
    config: RequestOptions & { url: string },
  ): Promise<RequestOptions & { url: string }> {
    const requestId = config.headers?.["X-Request-ID"] || nanoid(10);
    this.requestTimings.set(requestId, Date.now());

    // Ensure request ID is set
    config.headers = {
      ...config.headers,
      "X-Request-ID": requestId,
    };

    return config;
  }

  async onResponse<T>(
    response: ServiceResponse<T>,
  ): Promise<ServiceResponse<T>> {
    const requestId = response.meta?.timing?.requestId || "unknown";
    const startTime = this.requestTimings.get(requestId);

    if (startTime) {
      const duration = Date.now() - startTime;
      this.requestTimings.delete(requestId);

      // Add timing metadata
      response.meta = {
        ...response.meta,
        timing: {
          requestId,
          duration,
          timestamp: new Date().toISOString(),
        },
      };

      // Log slow requests
      if (duration > 5000) {
        // 5 seconds
        logger.warn("Slow request detected", {
          requestId,
          duration,
          status: response.status,
        });
      }
    }

    return response;
  }

  async onError(error: ServiceError): Promise<ServiceError> {
    const normalized = ErrorHandler.normalize(error);
    const requestId = normalized.requestId || "unknown";
    const startTime = this.requestTimings.get(requestId);

    if (startTime) {
      const duration = Date.now() - startTime;
      this.requestTimings.delete(requestId);

      logger.debug("Request failed", {
        requestId,
        duration,
        error: normalized.code,
      });
    }

    return normalized;
  }
}

/**
 * Retry interceptor for handling retryable errors
 */
export class RetryInterceptor implements ResponseInterceptor {
  name = "retry";
  priority = 200;

  constructor(
    private maxRetries: number = 3,
    private baseDelay: number = 1000,
  ) {}

  async onResponse<T>(
    response: ServiceResponse<T>,
  ): Promise<ServiceResponse<T>> {
    // Nothing to do for successful responses
    return response;
  }

  async onError(error: ServiceError): Promise<ServiceError> {
    // Build a new error instance with retry metadata instead of mutating
    const e = ErrorHandler.normalize(error);
    const details = {
      ...(e.details || {}),
      retryAttempt: e.details?.retryAttempt ?? 0,
      maxRetries: this.maxRetries,
    };

    return new UnifiedServiceError(
      e.message,
      (e.code as ServiceErrorCode) ?? ServiceErrorCode.UNKNOWN_ERROR,
      {
        status: e.status,
        details,
        service: e.service,
        context: e.context,
        requestId: e.requestId,
        retry: e.retry ?? true,
      },
    );
  }
}

/**
 * Security interceptor for adding security headers
 */
export class SecurityInterceptor implements RequestInterceptor {
  name = "security";
  priority = 150;

  async onRequest(
    config: RequestOptions & { url: string },
  ): Promise<RequestOptions & { url: string }> {
    config.headers = {
      ...config.headers,
      "X-Content-Type-Options": "nosniff",
      "X-Frame-Options": "DENY",
      "X-XSS-Protection": "1; mode=block",
    };

    return config;
  }
}

// RateLimitInterceptor was removed to avoid build-time 429 errors

/**
 * Response transformation interceptor
 */
export class TransformInterceptor implements ResponseInterceptor {
  name = "transform";
  priority = 25;

  async onResponse<T>(
    response: ServiceResponse<T>,
  ): Promise<ServiceResponse<T>> {
    // Add common response metadata if missing
    if (!response.meta) {
      response.meta = {};
    }

    // Normalize data structure for consistency
    if (typeof response.data === "object" && response.data !== null) {
      // Add common fields if they're missing
      if ("data" in response.data && "meta" in response.data) {
        // Response is already in normalized format
        return response;
      }
    }

    return response;
  }

  async onError(error: ServiceError): Promise<ServiceError> {
    // Normalize error format
    return ErrorHandler.normalize(error);
  }
}

/**
 * Interceptor manager for coordinating multiple interceptors
 */
export class InterceptorManager {
  private requestInterceptors: RequestInterceptor[] = [];
  private responseInterceptors: ResponseInterceptor[] = [];

  /**
   * Add request interceptor
   */
  addRequestInterceptor(interceptor: RequestInterceptor): void {
    this.requestInterceptors.push(interceptor);
    this.requestInterceptors.sort((a, b) => b.priority - a.priority);
    // logger.debug("Request interceptor added", {
    //   name: interceptor.name,
    //   priority: interceptor.priority,
    // });
  }

  /**
   * Add response interceptor
   */
  addResponseInterceptor(interceptor: ResponseInterceptor): void {
    this.responseInterceptors.push(interceptor);
    this.responseInterceptors.sort((a, b) => b.priority - a.priority);
    // logger.debug("Response interceptor added", {
    //   name: interceptor.name,
    //   priority: interceptor.priority,
    // });
  }

  /**
   * Process request through all interceptors
   */
  async processRequest(
    config: RequestOptions & { url: string },
  ): Promise<RequestOptions & { url: string }> {
    let processedConfig = config;

    for (const interceptor of this.requestInterceptors) {
      try {
        processedConfig = await interceptor.onRequest(processedConfig);
      } catch (error) {
        logger.error(`Request interceptor ${interceptor.name} failed`, {
          error,
        });
        if (interceptor.onError) {
          throw await interceptor.onError(ErrorHandler.normalize(error));
        }
        throw ErrorHandler.normalize(error);
      }
    }

    return processedConfig;
  }

  /**
   * Process response through all interceptors
   */
  async processResponse<T>(
    response: ServiceResponse<T>,
  ): Promise<ServiceResponse<T>> {
    let processedResponse = response;

    for (const interceptor of this.responseInterceptors) {
      try {
        processedResponse = await interceptor.onResponse(processedResponse);
      } catch (error) {
        logger.error(`Response interceptor ${interceptor.name} failed`, {
          error,
        });
        if (interceptor.onError) {
          throw await interceptor.onError(ErrorHandler.normalize(error));
        }
        throw ErrorHandler.normalize(error);
      }
    }

    return processedResponse;
  }

  /**
   * Process error through all interceptors
   */
  async processError(error: ServiceError): Promise<ServiceError> {
    let processedError: ServiceError = ErrorHandler.normalize(error);

    // Process through request interceptors first
    for (const interceptor of this.requestInterceptors) {
      if (interceptor.onError) {
        try {
          processedError = await interceptor.onError(processedError);
        } catch (err) {
          logger.error(
            `Request interceptor ${interceptor.name} error handler failed`,
            { error: err },
          );
        }
      }
    }

    // Then through response interceptors
    for (const interceptor of this.responseInterceptors) {
      if (interceptor.onError) {
        try {
          processedError = await interceptor.onError(processedError);
        } catch (err) {
          logger.error(
            `Response interceptor ${interceptor.name} error handler failed`,
            { error: err },
          );
        }
      }
    }

    return processedError;
  }

  /**
   * Remove interceptor by name
   */
  removeInterceptor(name: string): boolean {
    const requestIndex = this.requestInterceptors.findIndex(
      (i) => i.name === name,
    );
    const responseIndex = this.responseInterceptors.findIndex(
      (i) => i.name === name,
    );

    let removed = false;

    if (requestIndex !== -1) {
      this.requestInterceptors.splice(requestIndex, 1);
      removed = true;
    }

    if (responseIndex !== -1) {
      this.responseInterceptors.splice(responseIndex, 1);
      removed = true;
    }

    if (removed) {
      // logger.debug("Interceptor removed", { name });
    }

    return removed;
  }

  /**
   * Get list of all interceptors
   */
  getInterceptors(): { request: string[]; response: string[] } {
    return {
      request: this.requestInterceptors.map((i) => i.name),
      response: this.responseInterceptors.map((i) => i.name),
    };
  }

  /**
   * Clear all interceptors
   */
  clear(): void {
    this.requestInterceptors = [];
    this.responseInterceptors = [];
    // logger.debug("All interceptors cleared");
  }
}

// Create default interceptor manager with common interceptors
export function createDefaultInterceptorManager(): InterceptorManager {
  const manager = new InterceptorManager();

  // Add default interceptors
  manager.addRequestInterceptor(new TimingInterceptor());
  manager.addRequestInterceptor(new SecurityInterceptor());
  manager.addRequestInterceptor(new LoggingInterceptor());

  manager.addResponseInterceptor(new TimingInterceptor());
  manager.addResponseInterceptor(new TransformInterceptor());
  manager.addResponseInterceptor(new LoggingInterceptor());
  manager.addResponseInterceptor(new RetryInterceptor());

  return manager;
}

export { type RequestInterceptor, type ResponseInterceptor };
